import { c as _c } from "react/compiler-runtime";
// Connections (Concept connect-grants), as pieces of the Instances pages (components/settings.tsx):
// ConnectForm: Connect this home to another instance (OAuth through both auths: the remote's consent page opens, its
// callback lands back on Instances with ?connected=<R>). Opens as the `connect` panel from the Instances page's +;
// an invite on another instance sends its guests to ?connect=<origin> (auth:Decision/invite-links), prefilled.
// Grants: one instance's grants both ways (this home's key into it, its key into this home), each revocable at once.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Connection } from "@/api";
import { ErrorLine } from "@/components/error-line";
import { Field } from "@/components/field";
import { Group, Stack } from "@/components/group";
import { LinkButton } from "@/components/link-button";
import { Icon } from "@/components/icon";
import { errorText } from "@/store";
const day = (iso: string | null) => iso ? iso.slice(0, 16).replace("T", " ") : "—";

/** A name or a URL, as typed (auth maps a name by its deployment's pattern; no hosting account here) → the instance's base URL (auth resolves its issuer from it). */
export const instanceUrl = (v: string) => v.trim().replace(/\/+$/, "");

/** `initial`: an invite's origin (?connect=<origin>), or "1" = empty. `onDone`: connected without a redirect.
 *  Centred: an icon in a circle, the address field, the hint. (Letting someone in is Members \u203a Invite.) */
export function ConnectForm({
  initial,
  onDone
}: {
  initial: string;
  onDone: () => void;
}) {
  const {
    t
  } = useTranslation();
  const [url, setUrl] = useState(initial === "1" ? "" : initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // a pasted invite link (<origin>/auth/invite/<token>): accepting it sets a browser session on that origin, so it
  // opens there (top-level); its page sends you back here into Connect. `home` = this instance, for its form.
  const invite = /^https?:\/\/[^/]+\/auth\/invite\/[A-Za-z0-9_-]+\/?$/.test(url.trim());
  const openInvite = () => {
    const u = new URL(url.trim());
    u.searchParams.set("home", window.location.origin);
    window.location.href = u.toString();
  };
  const connect = async () => {
    if (invite) return openInvite();
    setBusy(true);
    setError(null);
    try {
      const {
        redirect
      } = await api.connections.connect(instanceUrl(url));
      if (redirect) window.location.href = redirect;else onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return <div className="connect" data-t="connect-form">
			<div className="badge-icon">
				<Icon name="link" size={26} />
			</div>
			<form onSubmit={e_0 => {
      e_0.preventDefault();
      void connect();
    }}>
				<Field t="connect-url" className="mono" placeholder={t("connections.placeholder")} aria-label={t("connections.placeholder")} value={url} onChange={e_1 => setUrl(e_1.target.value)} action={<LinkButton type="submit" data-t="connect" disabled={busy || !url.trim()}>
							{invite ? t("connections.openInvite") : t("connections.connectButton")}
						</LinkButton>} />
				<div className="muted note">{invite ? t("connections.inviteHint", {
          home: window.location.host
        }) : t("connections.hint")}</div>
			</form>
			{error && <ErrorLine>{error}</ErrorLine>}
		</div>;
}

/** One instance's grants, both directions, each with Disconnect. */
export function Grants(t0) {
  const $ = _c(17);
  const {
    grants,
    onChange
  } = t0;
  const {
    t
  } = useTranslation();
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== onChange || $[1] !== t) {
    t1 = async c => {
      if (!window.confirm(t("connections.confirmRevoke", {
        instance: c.instance
      }))) {
        return;
      }
      ;
      try {
        await api.connections.revoke(c.id);
        onChange();
      } catch (t2) {
        const e = t2;
        setError(errorText(e));
      }
    };
    $[0] = onChange;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const revoke = t1;
  let t2;
  if ($[3] !== grants || $[4] !== revoke || $[5] !== t) {
    let t3;
    if ($[7] !== revoke || $[8] !== t) {
      t3 = c_0 => <Stack key={c_0.id} data-id={c_0.id} data-status={c_0.status} title={t(`connections.${c_0.direction}`)} value={c_0.status === "active" ? <LinkButton data-t="revoke" onClick={() => void revoke(c_0)}>{t("connections.revoke")}</LinkButton> : <span className="muted">{t(`connections.${c_0.status}`)}</span>} sub={<div>{c_0.person && <span>{t(`connections.as.${c_0.direction}`, {
            person: c_0.person
          })} · </span>}<span className="mono">{c_0.scopes.join(" ")}</span><span> · {t("connections.used", {
            at: day(c_0.lastUsedAt)
          })}</span></div>} />;
      $[7] = revoke;
      $[8] = t;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t2 = grants.map(t3);
    $[3] = grants;
    $[4] = revoke;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <Group t="grants">{t2}</Group>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== error) {
    t4 = error && <ErrorLine>{error}</ErrorLine>;
    $[12] = error;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}